import React from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface PlanOption {
    id: number;
    title: string;
    price: string;
    yearly: string;
    recommended?: boolean;
    numUsers: string;
    storage: string;
    photoVideoStorage: string;
    securityEnhance: string;
}

interface SubscriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onPlanSelect: (planId: number) => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ isOpen, onClose, onPlanSelect }) => {
    const navigate = useNavigate();

    const plans: PlanOption[] = [
        {
            id: 1,
            title: 'Basic',
            price: 'Free',
            yearly: 'Free',
            numUsers: '1 Recipient',
            storage: '100MB',
            photoVideoStorage: 'Unlimited',
            securityEnhance: 'None',
        },
        {
            id: 2,
            title: 'Gold',
            price: '$9.95 / month',
            yearly: '$100 / year (16% off)',
            recommended: true,
            numUsers: '100 Recipients',
            storage: '1TB',
            photoVideoStorage: 'Unlimited',
            securityEnhance: 'Security Enhance',
        },
        {
            id: 3,
            title: 'Platinum',
            price: '$19.95 / month',
            yearly: '$175 / year (45% off)',
            numUsers: '1000 Recipients',
            storage: '2TB',
            photoVideoStorage: 'Unlimited',
            securityEnhance: 'Security Enhance',
        },
    ];

    const handleSelectPlan = (planId: number) => {
        onPlanSelect(planId);
        if (planId === 1) {
            // Free plan - just close modal
            onClose();
        } else {
            // Paid plan - navigate to payment
            navigate(`/add-card?plan=${planId}`);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b">
                    <h2 className="text-2xl font-bold text-gray-900">
                        Choose Your bEternal Plan
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <div className="text-center mb-8">
                        <h3 className="text-lg text-gray-600 mb-2">
                            Welcome! Let's get you started with the perfect plan for your needs.
                        </h3>
                        <p className="text-sm text-gray-500">
                            You can always upgrade or downgrade later.
                        </p>
                    </div>

                    {/* Plans Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {plans.map((plan) => (
                            <div
                                key={plan.id}
                                className={`bg-white border-2 rounded-lg p-6 relative transition-all hover:shadow-lg ${plan.recommended
                                        ? 'border-orange-500 shadow-md'
                                        : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                {plan.recommended && (
                                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                        <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                                            Recommended
                                        </span>
                                    </div>
                                )}

                                <div className="text-center">
                                    <h4 className="text-xl font-bold text-gray-900 mb-2">{plan.title}</h4>
                                    <p className="text-2xl font-bold text-gray-900 mb-1">{plan.price}</p>
                                    <p className="text-sm text-gray-500 mb-6">{plan.yearly}</p>

                                    <div className="space-y-4 mb-6">
                                        <div className="text-center">
                                            <p className="font-semibold text-gray-700">Number of users:</p>
                                            <p className="text-gray-600">{plan.numUsers}</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="font-semibold text-gray-700">Storage:</p>
                                            <p className="text-gray-600">{plan.storage}</p>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => handleSelectPlan(plan.id)}
                                        className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${plan.id === 1
                                                ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                : plan.recommended
                                                    ? 'bg-orange-500 text-white hover:bg-orange-600'
                                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                                            }`}
                                    >
                                        {plan.id === 1 ? 'Continue Free' : 'Select Plan'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Footer */}
                    <div className="mt-8 text-center text-sm text-gray-500">
                        <p>All plans include unlimited photo and video storage</p>
                        <p className="mt-1">Cancel anytime • No hidden fees • 30-day money-back guarantee</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionModal;